import React, { Dispatch, SetStateAction, useState } from 'react'
import { IQuestion } from '../../../classes/Question';
import { ISurvey } from '../../../classes/Survey';
import './ComparativeInformation.css';
import ReturnBar from '../../generic/ReturnBar/ReturnBar';
import { ComparativeInformationChart } from '../ComparativeInformationChart/ComparativeInformationChart';
import { experimentalStyled as styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';

interface Props {
    survey: ISurvey,
    //questions: IQuestion[],
    questions: any[],
    currentSurvey: IQuestion[],
    setViewComparative: Dispatch<SetStateAction<boolean>>,
    titleSurveyCurrent: string,
    comparisonsLoaded: boolean,
}

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#ffff',
    ...theme.typography.body2,
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    }));

    
const ComparativeInformation = (props: Props) => {
    const [filters, setFilters] = useState<{[key: string]: string}>({});
    
    console.log('[DEBUG ComparativeInfo] Rendering with:', {
        surveyTitle: props.survey.title,
        questionsCount: props.questions.length,
        dimensionsCount: props.survey.dimensions?.length,
        hasNativeResponses: !!(props.survey as any).native_responses,
        nativeResponsesCount: (props.survey as any).native_responses?.length || 0
    });
    
    const handleFilterChange = (dimensionName: string, newFilter: string) => {
        setFilters(prev => ({
            ...prev,
            [dimensionName]: newFilter
        }));
    };
    
    const scoreColor = (score: number, maxScore: number): string => {
        let color = "";
        
        const adjustedScore = props.survey.title === "Employee Burnout" ? score : score + 1;
        
        switch(props.survey.title) {
          case "Employee Burnout": //Burnout - inverted logic
            if(adjustedScore >= 1 && adjustedScore <= 1.62) color= "#6DAE43"; // Low burnout = good (green)
            if(adjustedScore >= 1.63 && adjustedScore <= 2.67) color= "#FDD835";
            if(adjustedScore >= 2.68 && adjustedScore <= 4) color= "#FB5523"; // High burnout = bad (red)
            break;
          case "Employee Engagement": //Engagement
            if(adjustedScore >= 1 && adjustedScore <= 2.33) color= "#FB5523";
            if(adjustedScore >= 2.34 && adjustedScore <= 3.67) color= "#FDD835";
            if(adjustedScore >= 3.67 && adjustedScore <= 5) color= "#6DAE43";
            break;
          case "Job Satisfaction Survey": //Job Satisfaction
            if(adjustedScore >= 1 && adjustedScore <= 3) color= "#FB5523";
            if(adjustedScore >= 3.1 && adjustedScore <= 5.1) color= "#FDD835";
            if(adjustedScore >= 5.1 && adjustedScore <= 7) color= "#6DAE43";
            break;
          default:
            const percentage = adjustedScore / maxScore;
            color = percentage > 0.33? percentage > 0.66? "#6DAE43" : "#FDD835" : "#FB5523";
        }
        return color;
      }
    
    // Check if we have historical data
    const hasHistoricalData = props.questions && props.questions.length > 0;

    return (
        <>
            <ReturnBar setToggle={props.setViewComparative} value={false} title="Dimensions" subtitle="Comparative information" />
            
            {/* Loading state */}
            {!props.comparisonsLoaded ? (
                <Item style={{ padding: '80px 40px', textAlign: 'center' }}>
                    <CircularProgress size={48} thickness={4} style={{ color: '#1A237E', marginBottom: '24px' }} />
                    <div style={{
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        fontSize: "16px",
                        color: '#666'
                    }}>
                        Loading historical data...
                    </div>
                </Item>
            ) : !hasHistoricalData ? (
                /* No data state */
                <Item style={{ padding: '40px', textAlign: 'center' }}>
                    <div style={{
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        fontSize: "18px",
                        color: '#666',
                        marginBottom: '16px'
                    }}>
                        No historical data available
                    </div>
                    <div style={{
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        fontSize: "14px",
                        color: '#999'
                    }}>
                        Dimension trends will appear here once you have multiple survey responses over time
                    </div>
                </Item>
            ) : (
                /* Data loaded - show charts */
                <div className="flex">
                    {
                        props.survey.dimensions.map((dimension, index) => {
                            const currentFilter = filters[dimension.name] || 'week';
                            // Map dimension names for display
                            const displayName = dimension.name === 'Employee Engagement' ? 'Employee Disengagement' : dimension.name;
                            return (
                                <Item key={index}>
                                <div>
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px'}}>
                                        <div>
                                            <h3 style={{margin: 0}}>{displayName}</h3>
                                            <div style={{
                                                fontFamily: "'IBM Plex Sans', sans-serif",
                                                fontSize: "32px",
                                                color: scoreColor(dimension.score, dimension.maxScore),
                                                fontWeight: "600",
                                                marginTop: '8px'
                                            }} >
                                                {dimension.score.toFixed(2)}
                                            </div>
                                        </div>
                                        <ComparativeInformationChart 
                                            survey={props.survey} 
                                            questions={props.questions} 
                                            currentSurvey={props.currentSurvey} 
                                            titleSurveyCurrent={props.titleSurveyCurrent} 
                                            dimension={dimension.name}
                                            currentSurveyId={props.survey.id}
                                            isFilterOnly={true}
                                            filter={currentFilter}
                                            onFilterChange={(newFilter) => handleFilterChange(dimension.name, newFilter)}
                                        />
                                    </div>
                                    <ComparativeInformationChart 
                                        survey={props.survey} 
                                        questions={props.questions} 
                                        currentSurvey={props.currentSurvey} 
                                        titleSurveyCurrent={props.titleSurveyCurrent} 
                                        dimension={dimension.name}
                                        currentSurveyId={props.survey.id}
                                        isFilterOnly={false}
                                        filter={currentFilter}
                                        onFilterChange={(newFilter) => handleFilterChange(dimension.name, newFilter)}
                                    />
                                </div>
                                </Item>
                            )
                        })
                    }
                </div>
            )}
        </>
    )
}

export default ComparativeInformation;